// components/ui/tooltip.tsx

"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";
import { composeRefs } from "@/lib/ui/compose-refs";

const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

/** Vrai dès qu'un `TooltipProvider` de CE module est ancêtre. Radix n'expose
 *  pas cette information et sa `Root` **lève** sans provider — ce qui ferait
 *  planter tout arbre rendu hors du layout racine (un test de composant, et
 *  surtout `global-error.tsx`, qui REMPLACE le layout racine en production).
 *  `Tip` s'en sert pour poser son propre provider en dernier recours. */
const HasTooltipProvider = React.createContext(false);

/** Les réglages du tooltip de l'app, en un seul endroit. `disableHoverableContent`
 *  reproduit le `title` natif : la bulle se ferme dès que le pointeur quitte
 *  l'élément, elle n'est jamais une surface qu'on survole. */
function TooltipProvider({
  children,
  delayDuration = 200,
  skipDelayDuration = 300,
  disableHoverableContent = true,
  ...rest
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <HasTooltipProvider.Provider value={true}>
      <TooltipPrimitive.Provider
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
        disableHoverableContent={disableHoverableContent}
        {...rest}
      >
        {children}
      </TooltipPrimitive.Provider>
    </HasTooltipProvider.Provider>
  );
}

type ContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  bubbleClassName?: string;
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  ContentProps
>(
  (
    {
      className,
      bubbleClassName,
      side = "top",
      align = "center",
      sideOffset = 8,
      children,
      ...props
    },
    ref
  ) => {
    // Bulle sombre à flèche, volontairement légère : elle apparaît des
    // centaines de fois dans l'app, donc elle se lit comme une info de chrome
    // et jamais comme une surface d'UI. `max-w` + `leading-snug` reproduisent
    // le retour à la ligne que le tooltip natif faisait tout seul — sans eux,
    // une phrase d'explication de refus sortirait de l'écran sur une ligne.
    const bubbleBase =
      "z-50 max-w-[260px] rounded-lg px-3 py-1.5 text-[12px] leading-snug shadow-lg " +
      "border border-transparent bg-neutral-900 text-white";

    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          side={side}
          align={align}
          sideOffset={sideOffset}
          avoidCollisions
          className={cn(
            "will-change-[transform,opacity] ease-out duration-100",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
            bubbleBase,
            bubbleClassName,
            className
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow width={12} height={6} className="fill-neutral-900" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  }
);
TooltipContent.displayName = "TooltipContent";

/* ------------------------------------------------------------------ *
 * Tip — le remplaçant de l'attribut `title` natif
 * ------------------------------------------------------------------ */

/** Éléments dont l'attribut `title` servait de NOM accessible quand ils
 *  n'ont pas de texte visible (bouton-icône). Sur un `<span>` ou un `<div>`,
 *  `title` n'était qu'une info de survol : la remplacer par une description
 *  (`aria-describedby`, posé par Radix) est le bon geste, pas un nom. */
const NAMED_BY_TITLE = new Set(["button", "a", "input", "select", "textarea", "summary", "area"]);

/** Un enfant porte-t-il du texte visible ? Sert à distinguer le bouton-icône
 *  (qui perd son nom accessible si on retire `title`) du bouton libellé. */
function hasVisibleText(node: React.ReactNode): boolean {
  if (node === null || node === undefined || typeof node === "boolean") return false;
  if (typeof node === "string") return node.trim().length > 0;
  if (typeof node === "number") return true;
  if (Array.isArray(node)) return node.some(hasVisibleText);
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode; "aria-hidden"?: unknown };
    if (props["aria-hidden"] === true || props["aria-hidden"] === "true") return false;
    return hasVisibleText(props.children);
  }
  return false;
}

function needsAccessibleName(el: React.ReactElement): boolean {
  const props = el.props as Record<string, unknown>;
  if (props["aria-label"] != null || props["aria-labelledby"] != null) return false;
  const named =
    typeof el.type === "string"
      ? NAMED_BY_TITLE.has(el.type)
      : true; // composant : il reporte ses props sur son propre élément interactif
  if (!named && props.onClick == null && props.role == null) return false;
  return !hasVisibleText(props.children as React.ReactNode);
}

/**
 * Un enfant qui PEUT être désactivé a besoin du `<span>` d'enveloppe.
 *
 * 🔴 On teste la PRÉSENCE de la prop, jamais sa valeur. Décider d'après
 * `disabled === true` ferait apparaître et disparaître le span au fil des
 * bascules — donc **remonterait l'élément** à chaque fois (React voit deux
 * arbres différents). Sur un bouton c'est un scintillement et une fenêtre de
 * course ; sur un champ ça effacerait une saisie non contrôlée. La présence de
 * la prop, elle, ne change pas d'un rendu à l'autre.
 */
function needsHoverWrapper(el: React.ReactElement): boolean {
  const props = el.props as Record<string, unknown>;
  return props.disabled !== undefined || props["aria-disabled"] !== undefined;
}

/* ------------------------------------------------------------------ *
 * Composition — `Tip` doit REPORTER sur son enfant les props qu'on lui pose
 * ------------------------------------------------------------------ */

/**
 * 🔴 `Tip` est très souvent l'enfant d'un déclencheur Radix `asChild`
 * (`<PopoverTrigger asChild><Tip …><button/></Tip></PopoverTrigger>`). Radix
 * **clone alors `Tip`** en lui passant `onClick`, `ref`, `aria-expanded`,
 * `aria-haspopup`… : un composant fonction qui ne déstructure que ses propres
 * props les jette **en silence**, et le déclencheur devient MORT — le clic ne
 * fait littéralement rien.
 *
 * Le défaut est invisible aux deux filets habituels : `TipProps` est un type
 * FERMÉ, donc le typecheck ne voit jamais ces props (elles n'existent qu'au
 * runtime, injectées par `cloneElement`), et le rendu reste parfaitement
 * normal — seul le comportement manque. Mesuré le 2026-08-16 : sur la pastille
 * de couleur, `data-state` (posé par le déclencheur du TOOLTIP) était bien sur
 * le bouton, mais `aria-expanded`/`aria-haspopup` (posés par celui du POPOVER)
 * étaient absents.
 */
// `composeRefs` vit désormais dans `lib/ui/compose-refs.ts` (source unique).

/**
 * Fusionne les props reçues (celles du déclencheur) avec celles que l'enfant
 * porte déjà — mêmes règles que le `Slot` de Radix, pour qu'intercaler `Tip`
 * soit rigoureusement transparent :
 *   - les gestionnaires `on*` se CHAÎNENT (celui de l'enfant d'abord), sinon
 *     envelopper un bouton dans un tooltip lui retirerait son propre `onClick` ;
 *   - `className` et `style` se cumulent ;
 *   - `ref` se compose ;
 *   - tout le reste laisse la main à l'enfant (son `aria-label` explicite gagne).
 */
function mergeForwardedProps(
  forwarded: Record<string, unknown>,
  own: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...forwarded, ...own };

  for (const key of Object.keys(forwarded)) {
    const fromTrigger = forwarded[key];
    const fromChild = own[key];

    if (/^on[A-Z]/.test(key) && typeof fromTrigger === "function") {
      merged[key] =
        typeof fromChild === "function"
          ? (...args: unknown[]) => {
              (fromChild as (...a: unknown[]) => unknown)(...args);
              (fromTrigger as (...a: unknown[]) => unknown)(...args);
            }
          : fromTrigger;
    } else if (key === "className") {
      merged.className = cn(fromTrigger as string, fromChild as string);
    } else if (key === "style") {
      merged.style = {
        ...(fromTrigger as React.CSSProperties),
        ...(fromChild as React.CSSProperties),
      };
    } else if (key === "ref") {
      merged.ref = composeRefs(
        fromTrigger as React.Ref<unknown>,
        fromChild as React.Ref<unknown>,
      );
    }
  }

  return merged;
}

type TipProps = {
  /** Le texte de la bulle. `null` / `undefined` / `""` ⇒ aucun tooltip, et
   *  l'enfant est rendu tel quel (pas de nœud Radix monté pour rien) — c'est
   *  le cas courant `title={condition ? "…" : undefined}`. */
  content: React.ReactNode;
  children: React.ReactNode;
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>["side"];
  align?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>["align"];
  sideOffset?: number;
  delayDuration?: number;
  /** Classes sur la bulle. */
  className?: string;
  /** Classes sur le `<span>` d'enveloppe — monté UNIQUEMENT quand l'enfant est
   *  désactivé (cf. plus bas). À poser quand ce span devient l'élément de flux
   *  à la place de l'enfant (`w-full`, `flex-1`…). */
  wrapperClassName?: string;
};

/**
 * Remplace l'attribut `title` natif par notre bulle.
 *
 * ```tsx
 * <Tip content="Copier le lien">
 *   <button onClick={copy}><Copy className="h-4 w-4" /></button>
 * </Tip>
 * ```
 *
 * Trois choses que le `title` natif ne faisait pas :
 *
 * 1. 🔴 **Il s'affiche sur un élément DÉSACTIVÉ.** `buttonVariants` pose
 *    `disabled:pointer-events-none`, donc aucun événement de survol n'atteint
 *    un bouton grisé : son `title` était structurellement muet (cf.
 *    `docs/architecture/ui-conventions.md`). Quand l'enfant est désactivé, on
 *    monte un `<span>` d'enveloppe qui, lui, reçoit le survol — c'est ce qui
 *    rend enfin lisible « Connectez votre page Facebook pour publier dessus. »
 * 2. **Il ne perd pas le nom accessible.** Sur un bouton-icône sans
 *    `aria-label`, `title` ÉTAIT le nom lu par les lecteurs d'écran : on le
 *    repose en `aria-label` (Radix, lui, ne pose qu'un `aria-describedby`,
 *    qui est une description, pas un nom).
 * 3. **Il ne monte rien quand il n'y a rien à dire** (`content` vide).
 *
 * L'enfant doit être un élément unique qui accepte une `ref` (élément DOM ou
 * composant qui reporte `ref`) : Radix s'y ancre pour positionner la bulle.
 */
function Tip(props: TipProps) {
  const {
    content,
    children,
    side = "top",
    align = "center",
    sideOffset = 8,
    delayDuration,
    className,
    wrapperClassName,
    // Tout le reste vient d'un déclencheur Radix `asChild`, jamais d'un
    // appelant : le cast élargit la vue du runtime SANS ouvrir `TipProps`,
    // qui doit rester fermé pour que les fautes de frappe (`sideOfset`)
    // restent des erreurs de compilation sur les centaines d'appels.
    ...forwarded
  } = props as TipProps & Record<string, unknown>;

  const hasProvider = React.useContext(HasTooltipProvider);
  const child = React.isValidElement(children) ? children : null;
  const isForwarding = Object.keys(forwarded).length > 0;

  /** Reporte les props du déclencheur sur l'élément qui porte réellement
   *  l'interaction. Sans ce geste, intercaler `Tip` DÉSARME le déclencheur. */
  const forwardOnto = (el: React.ReactElement) =>
    isForwarding
      ? React.cloneElement(
          el as React.ReactElement<Record<string, unknown>>,
          mergeForwardedProps(forwarded, el.props as Record<string, unknown>),
        )
      : el;

  if (content === null || content === undefined || content === false || content === "" || !child) {
    // Le cas courant `content={cond ? "…" : undefined}` : pas de bulle, mais
    // le déclencheur qui nous enveloppe doit rester vivant.
    return child ? forwardOnto(child) : <>{children}</>;
  }

  const labelled =
    typeof content === "string" && needsAccessibleName(child)
      ? React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
          "aria-label": content,
        })
      : child;

  // Les props vont sur le nœud le plus EXTERNE — le même que celui auquel
  // Radix ancre la bulle. Quand l'enfant est désactivé, c'est le `<span>` :
  // un bouton grisé n'émet aucun événement, mais son enveloppe si.
  const trigger = forwardOnto(
    needsHoverWrapper(child) ? (
      <span className={cn("inline-flex", wrapperClassName)}>{labelled}</span>
    ) : (
      labelled
    ),
  );

  const body = (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
        {content}
      </TooltipContent>
    </Tooltip>
  );

  // Le provider global d'`app/layout.tsx` couvre l'app entière ; ce repli sert
  // les arbres qui n'en héritent pas (tests de composant, `global-error.tsx`).
  // Sans lui, `Tip` ne dégrade pas : il LÈVE, et emporte tout l'écran.
  return hasProvider ? body : <TooltipProvider>{body}</TooltipProvider>;
}

export { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent, Tip };
